function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

class ProductSwatches extends HTMLElement {
    constructor() {
        super();
        this.productCard = this.closest('[data-js-product-card]');
        this.variants = JSON.parse(this.querySelector('[data-js-variants]').textContent);
        this.swatches = this.querySelectorAll('[data-js-swatch]');
        this.sizeSelect = this.querySelector('[data-js-size-select]');
        this.form = this.closest('form');
        this.hiddenInput = this.form?.querySelector('[data-js-hidden-input]');
        this.availabilityDateInput = this.form?.querySelector('[data-js-availability-date]');
        this.selectedColor = this.swatches[0]?.getAttribute('data-color') || '';
        this.swatchesContainer = this.querySelector('[data-js-swatches]');
        this.swatchesOpener = this.querySelector('[data-js-swatch-opener]');
        this.swatchCounter = this.querySelector('[data-js-swatch-counter]');
        this.swatchTooltip = this.querySelector('[data-js-swatch-tooltip]');
        this.primaryImageElement = this.productCard.querySelector('.card-product__image-primary.firstImage');
        this.hoverImageElement = this.productCard.querySelector('.card-product__image-primary.imgHover');
        this.priceElement = this.productCard.querySelector('[data-js-price]');
        this.oldPriceElement = this.productCard.querySelector('[data-js-old-price]');
        this.discountPriceElement = this.productCard.querySelector('[data-js-discount-price]');
        this.discountOldPriceElement = this.productCard.querySelector('[data-js-discount-old-price]');
        this.compareAtPriceElement = this.productCard.querySelector('[data-js-compare-at-price]');
        this.discountPercentageElement = this.productCard.querySelector('[data-js-discount-percentage]');

        this.setupEventListeners();
        this.updateSizeSelect(this.selectedColor);
        this.updateHiddenInput();
        this.updateAvailabilityDate();
        this.updateSwatchCounter();

        // 3D icon (aggiunta)
        this.dddIcon = this.productCard?.querySelector('[data-js-3d-icon]');
        // 3D: stato iniziale icona
        this.update3DIcon(this.selectedColor);
    }

    getAvailableSizes(color) {
        return this.variants.variants.filter(v =>
            v.option1 === color
        );
    }

    updateSizeSelect(color) {
        if (!this.hiddenInput || !this.sizeSelect) return;

        const sizes = this.getAvailableSizes(color);
        // if sizes.option2 is a number, order the sizes by number
        sizes.sort((a, b) => {
            const aNum = parseInt(a.option2);
            const bNum = parseInt(b.option2);
            return aNum - bNum;
        });
        this.sizeSelect.innerHTML = '<option value="" disabled selected>Size</option>';
        let firstVariantId = '';
        sizes.forEach((variant, idx) => {
            if (!variant.option2) return;
            const opt = document.createElement('option');
            opt.value = variant.id;
            opt.textContent = variant.option2;
            this.sizeSelect.appendChild(opt);
            if (firstVariantId === '') firstVariantId = variant.id;
        });
        // Show or hide the size select
        if (this.sizeSelect.options.length > 1) {
            this.sizeSelect.classList.remove('hidden');
        } else {
            this.sizeSelect.classList.add('hidden');
        }
        // Always preselect the first available size if any
        if (firstVariantId) {
            this.sizeSelect.value = firstVariantId;
            this.hiddenInput.value = firstVariantId;
        } else {
            this.hiddenInput.value = '';
        }
    }

    toggleSwatches() {
        this.swatchesOpener.querySelector('svg').classList.toggle('-rotate-90');
        this.swatchesOpener.querySelector('svg').classList.toggle('rotate-90');
        this.swatchesContainer.classList.toggle('h-8');
        this.swatchCounter.classList.toggle('hidden');
    }

    updateHiddenInput() {
        if (!this.hiddenInput) return;
        if (this.sizeSelect.value) {
            this.hiddenInput.value = this.sizeSelect.value;
        } else {
            // If no size select, pick the first available variant for the color
            const v = this.getAvailableSizes(this.selectedColor)[0];
            this.hiddenInput.value = v ? v.id : '';
        }
    }

    getSelectedVariant() {
        if (!this.hiddenInput?.value || !this.variants?.variants?.length) return null;
        const selectedId = String(this.hiddenInput.value);
        return this.variants.variants.find(v => String(v.id) === selectedId) || null;
    }

    updateAvailabilityDate() {
        if (!this.availabilityDateInput) return;

        const selectedVariant = this.getSelectedVariant();
        const availabilityDate = selectedVariant?.availability_date;

        if (availabilityDate) {
            this.availabilityDateInput.value = availabilityDate;
            this.availabilityDateInput.disabled = false;
        } else {
            this.availabilityDateInput.value = '';
            this.availabilityDateInput.disabled = true;
        }
    }

    updateProductImage(color) {

        // Find the first variant with this color
        const variantWithColor = this.variants.variants.find(v => v.option1 === color);

        if (!variantWithColor || !variantWithColor.featured_image) return;

        // First set both primary and hover images to the primary image
        // This ensures we don't see a loading state when hovering
        if (this.primaryImageElement) {
            this.primaryImageElement.setAttribute('src', variantWithColor.featured_image);
            this.primaryImageElement.setAttribute('alt', variantWithColor.featured_image_alt);

            // Update srcset if it exists
            if (this.primaryImageElement.hasAttribute('srcset')) {
                const newSrcset = variantWithColor.featured_image.replace('&width=800', '&width=352') + ' 352w, ' +
                    variantWithColor.featured_image + ' 800w';
                this.primaryImageElement.setAttribute('srcset', newSrcset);
            }
        }

        // Also set hover image to primary image first as a fallback
        if (this.hoverImageElement) {
            this.hoverImageElement.setAttribute('src', variantWithColor.featured_image);
            this.hoverImageElement.setAttribute('alt', variantWithColor.featured_image_alt_hover);

            if (this.hoverImageElement.hasAttribute('srcset')) {
                const newSrcset = variantWithColor.featured_image.replace('&width=800', '&width=352') + ' 352w, ' +
                    variantWithColor.featured_image + ' 800w';
                this.hoverImageElement.setAttribute('srcset', newSrcset);
            }

            // Then update hover image with DETT1 version (after a brief delay to ensure primary is loaded), check first if the DETT1 image is not a 404
            const dett1Image = variantWithColor.featured_image.replace('_1_500x', '_DETT1');
            const dett1ImageUrl = new URL("https:" + dett1Image);
            const checkDett1Image = async () => {
                const dett1ImageResponse = await fetch(dett1ImageUrl);
                if (dett1ImageResponse.ok) {
                    this.hoverImageElement.setAttribute('src', dett1Image);

                    // Update hover image srcset if needed
                    if (this.hoverImageElement.hasAttribute('srcset')) {
                        const newHoverSrcset = dett1Image.replace('&width=800', '&width=352') + ' 352w, ' +
                            dett1Image + ' 800w';
                        this.hoverImageElement.setAttribute('srcset', newHoverSrcset);
                    }
                }
            }
            checkDett1Image();
        }
    }

    updateProductLink(color) {
        const variantWithColor = this.variants.variants.find(v => v.option1 === color);
        if (!variantWithColor || !variantWithColor.featured_image) return;
        const productLink = this.productCard.querySelector('.card-product__link');
        productLink.setAttribute('href', variantWithColor.url);
    }

    updateTooltip(swatch) {
        if (!swatch) {
            this.swatchTooltip.classList.add('hidden');
            return;
        }
        this.swatchTooltip.textContent = swatch.getAttribute('data-color-name');
        this.swatchTooltip.classList.remove('hidden');
        this.swatchTooltip.style.top = swatch.offsetTop + 'px';
        this.swatchTooltip.style.left = swatch.offsetLeft + 'px';
    }

    updateSwatchCounter() {
        if (this.swatches.length === 0) return;

        const firstTop = this.swatches[0].offsetTop;
        let hiddenCount = 0;
        this.swatches.forEach(swatch => {
            if (swatch.offsetTop > firstTop) hiddenCount++;
        });

        if (hiddenCount > 0) {
            this.swatchCounter.textContent = "+" + hiddenCount;
        }
    }

    updatePrice(color) {
        const variantWithColor = this.variants.variants.find(v => v.option1 === color);
        const variantPrice = variantWithColor.price;
        const variantPriceValue = variantWithColor.price_value;

        const variantCompareAtPrice = variantWithColor.compare_at_price;
        const variantCompareAtPriceValue = variantWithColor.compare_at_price_value;
        const variantCompareAtPricePercentage = variantWithColor.compare_percentage || 0;

        const variantDiscountPercentage = variantWithColor.discount_percentage;
        const variantDiscountPrice = variantWithColor.discount_price;

        if (variantCompareAtPriceValue > variantPriceValue) {

            if (this.priceElement) {
                this.priceElement.textContent = variantPrice;
                this.priceElement.classList.remove('hidden')
            }

            if (this.oldPriceElement) {
                this.oldPriceElement.textContent = variantCompareAtPrice;
                this.oldPriceElement.classList.remove('hidden')
            }

            if (this.discountPercentageElement) {
                this.discountPercentageElement.querySelectorAll('span').forEach(span => span.textContent = variantCompareAtPricePercentage + "%")
                this.discountPercentageElement.classList.remove('hidden')
            }



        } else if (variantDiscountPrice) {

            if (this.priceElement) {
                this.priceElement.textContent = variantDiscountPrice;
                this.priceElement.classList.remove('hidden')
            }

            if (this.oldPriceElement) {
                if (variantDiscountPrice != variantPrice) {
                    this.oldPriceElement.textContent = variantPrice;
                    this.oldPriceElement.classList.remove('hidden')
                } else {
                    this.oldPriceElement.classList.add('hidden')
                }

            }

            if (this.discountPercentageElement) {
                if (variantDiscountPrice != variantPrice) {
                    this.discountPercentageElement.querySelectorAll('span').forEach(span => span.textContent = "-" + variantDiscountPercentage + "%")
                    this.discountPercentageElement.classList.remove('hidden')
                } else {
                    this.discountPercentageElement.classList.add('hidden')
                }
            }

        } else {

            if (this.priceElement) {
                this.priceElement.textContent = variantPrice;
                this.priceElement.classList.remove('hidden')
            }

            if (this.oldPriceElement) {
                this.oldPriceElement.classList.add('hidden')
            }

            if (this.discountPercentageElement) {
                this.discountPercentageElement.classList.add('hidden')
            }
        }
    }

    // ==== 3D: toggle icona in base al colore ====
    update3DIcon(color) {
        if (!this.dddIcon || !this.variants?.variants?.length) return;

        const variantWithColor = (this.variants.variants || []).find(v => v.option1 === color);

        // se non trovo variante => nascondo
        if (!variantWithColor) {
            this.dddIcon.classList.add('hidden');
            this.dddIcon.setAttribute('aria-hidden', 'true');
            this.dddIcon.style.display = 'none';
            return;
        }

        // se la variante ha il modello 3D, mostro; altrimenti nascondo
        const has3D = variantWithColor.ddd_model === true;

        if (has3D) {
            this.dddIcon.classList.remove('hidden');
            this.dddIcon.setAttribute('aria-hidden', 'false');
            this.dddIcon.style.display = ''; // lascia al CSS il display di default
        } else {
            this.dddIcon.classList.add('hidden');
            this.dddIcon.setAttribute('aria-hidden', 'true');
            this.dddIcon.style.display = 'none';
        }
    }

    setupEventListeners() {
        this.swatches.forEach(swatch => {
            swatch.addEventListener('click', this.onSwatchClick.bind(this));
        });

        if (window.innerWidth > 1023) {
            this.swatches.forEach(swatch => {
                swatch.addEventListener('mouseenter', () => {
                    this.updateTooltip(swatch);
                });
                swatch.addEventListener('mouseleave', () => {
                    this.updateTooltip(null);
                });
            });
        }

        this.sizeSelect.addEventListener('change', () => {
            this.updateHiddenInput();
            this.updateAvailabilityDate();
        });

        this.swatchesOpener.addEventListener('click', () => {
            this.toggleSwatches();
        });

        window.addEventListener('resize', debounce(() => {
            this.updateSwatchCounter();
        }, 100));
    }

    onSwatchClick(event) {
        const swatch = event.currentTarget;
        this.swatches.forEach(s => s.classList.remove('selected'));
        swatch.classList.add('selected');

        this.selectedColor = swatch.getAttribute('data-color');
        this.updateSizeSelect(this.selectedColor);
        this.updateProductImage(this.selectedColor);
        this.updateProductLink(this.selectedColor);
        this.updateHiddenInput();
        this.updateAvailabilityDate();
        this.updatePrice(this.selectedColor);

        // 3D: aggiorna icona al cambio colore
        this.update3DIcon(this.selectedColor);
    }

}

customElements.define('product-swatches', ProductSwatches);